<template>
  <div class="px-4 py-5 space-y-4">
    <div>
      <h1 class="text-lg font-extrabold text-slate-800 text-left">Applicants</h1>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label class="field-label" for="applicants-job-filter">Select Job</label>
        <select
          id="applicants-job-filter"
          v-model="selectedRoleKey"
          class="input-field"
          :disabled="!roles.length || loadingRoles"
          @change="onJobSelected"
        >
          <option v-if="!roles.length" value="" disabled>
            {{ loadingRoles ? 'Loading jobs…' : 'No jobs found' }}
          </option>
          <option v-for="role in roles" :key="role.key" :value="role.key">
            {{ role.title }}
          </option>
        </select>
      </div>

      <div>
        <label class="field-label" for="applicants-status-filter">Status</label>
        <select id="applicants-status-filter" v-model="statusFilter" class="input-field">
          <option value="">All</option>
          <option value="submitted">New</option>
          <option value="viewed">Viewed</option>
          <option value="contacted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
    </div>

    <LoadingState
      :loading="loading"
      :message="!loading && !filteredApplicants.length ? emptyMessage : error"
    />

    <div
      v-if="!loading && filteredApplicants.length"
      class="rounded-xl border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] bg-white"
    >
      <div class="divide-y divide-slate-100">
        <div v-for="app in filteredApplicants" :key="app.id">
          <button
            type="button"
            class="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50/80 transition"
            @click="openApplicant(app)"
          >
            <div class="min-w-0 flex-1">
              <div class="text-sm font-bold text-slate-800 truncate">{{ app.name }}</div>
              <div class="text-xs text-slate-500 mt-0.5 truncate" :title="app.message || ''">
                {{ app.message || '—' }}
              </div>
            </div>

            <span class="status-pill shrink-0" :class="appStatusClass(app.status)">
              {{ appStatusLabel(app.status) }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="selectedApplicant"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4"
      @click.self="closeApplicant"
    >
      <div class="card-panel w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5 space-y-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-base font-bold text-slate-800 truncate">{{ selectedApplicant.name }}</h2>
            <p class="text-xs text-slate-500 mt-0.5">{{ formatDate(selectedApplicant.created_at) }}</p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span class="status-pill" :class="appStatusClass(selectedApplicant.status)">
              {{ appStatusLabel(selectedApplicant.status) }}
            </span>
            <button
              type="button"
              class="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 font-bold"
              aria-label="Close"
              @click="closeApplicant"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="text-xs text-slate-600 space-y-1.5">
          <p v-if="selectedApplicant.phone">
            Phone:
            <a :href="`tel:${selectedApplicant.phone}`" class="text-[#0d4169] font-semibold">{{ selectedApplicant.phone }}</a>
          </p>
          <p v-if="selectedApplicant.telegram_username">
            Telegram:
            <a
              :href="`https://t.me/${selectedApplicant.telegram_username}`"
              target="_blank"
              rel="noopener"
              class="text-[#0d4169] font-semibold"
            >
              @{{ selectedApplicant.telegram_username }}
            </a>
          </p>
          <p v-if="selectedApplicant.message" class="whitespace-pre-wrap leading-relaxed">{{ selectedApplicant.message }}</p>
          <div v-if="cvFilesFor(selectedApplicant).length" class="space-y-1.5 pt-1">
            <p class="font-semibold text-slate-700">Uploaded files</p>
            <div
              v-for="file in cvFilesFor(selectedApplicant)"
              :key="file.path"
              class="flex items-start gap-2"
            >
              <a
                v-if="file.isImage"
                :href="file.url"
                target="_blank"
                rel="noopener"
                class="block"
              >
                <img
                  :src="file.url"
                  :alt="file.name"
                  class="h-16 w-16 rounded-md object-cover border border-slate-200"
                />
              </a>
              <a
                :href="file.url"
                target="_blank"
                rel="noopener"
                class="inline-flex text-[#0d4169] font-bold underline break-all"
              >
                {{ file.name }}
              </a>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            class="px-3 py-2 rounded-lg font-bold border bg-emerald-50 text-emerald-700 border-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="busyForId === selectedApplicant.id || selectedApplicant.status === 'contacted' || selectedApplicant.status === 'rejected'"
            @click="setStatus(selectedApplicant, 'contacted')"
          >
            Accept
          </button>
          <button
            type="button"
            class="px-3 py-2 rounded-lg font-bold border bg-rose-50 text-rose-700 border-rose-600 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="busyForId === selectedApplicant.id || selectedApplicant.status === 'rejected' || selectedApplicant.status === 'contacted'"
            @click="setStatus(selectedApplicant, 'rejected')"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../utils/api'
import { mapCvFilesForDisplay } from '../utils/cvFiles'
import LoadingState from '../components/LoadingState.vue'

function roleKey(jobId, entryIndex) {
  return `${jobId}:${entryIndex}`
}

export default {
  name: 'ApplicantsView',
  components: { LoadingState },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const job = ref(null)
    const roles = ref([])
    const applicants = ref([])
    const loadingRoles = ref(true)
    const loading = ref(true)
    const error = ref('')
    const selectedApplicant = ref(null)
    const busyForId = ref(null)
    const selectedRoleKey = ref('')
    const statusFilter = ref('')

    const filteredApplicants = computed(() => {
      if (!statusFilter.value) return applicants.value
      return applicants.value.filter((app) => app.status === statusFilter.value)
    })

    const emptyMessage = computed(() => {
      if (error.value) return error.value
      if (!applicants.value.length) return 'No applicants yet.'
      return 'No applicants with this status.'
    })

    function formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    }
    function cvFilesFor(app) {
      return mapCvFilesForDisplay(app)
    }

    function appStatusLabel(status) {
      if (status === 'submitted') return 'New'
      if (status === 'viewed') return 'Viewed'
      if (status === 'contacted') return 'Accepted'
      if (status === 'rejected') return 'Rejected'
      return status || '—'
    }

    function appStatusClass(status) {
      if (status === 'submitted') return 'bg-[#e9f2f8] text-[#0d4169]'
      if (status === 'viewed') return 'bg-sky-100 text-sky-800'
      if (status === 'contacted') return 'bg-emerald-100 text-emerald-800'
      if (status === 'rejected') return 'bg-rose-100 text-rose-700'
      return 'bg-slate-100 text-slate-700'
    }

    function parseRoleKey(key) {
      const [jobIdRaw, entryRaw] = String(key || '').split(':')
      const jobId = Number(jobIdRaw)
      const entryIndex = Math.max(0, Number(entryRaw) || 0)
      if (!Number.isFinite(jobId) || jobId <= 0) return null
      return { jobId, entryIndex }
    }

    async function loadRoles() {
      loadingRoles.value = true
      try {
        const res = await apiFetch('/jobs/mine/applicant-roles')
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to load jobs')

        roles.value = (data.roles || []).map((role) => ({
          ...role,
          key: roleKey(role.job_id, role.entry_index),
        }))

        const routeJobId = Number(route.params.id)
        const routeEntryRaw = route.query.entry
        const routeEntry =
          routeEntryRaw === undefined || routeEntryRaw === ''
            ? 0
            : Math.max(0, Number(routeEntryRaw) || 0)
        const preferredKey = Number.isFinite(routeJobId) && routeJobId > 0
          ? roleKey(routeJobId, routeEntry)
          : ''

        if (preferredKey && roles.value.some((r) => r.key === preferredKey)) {
          selectedRoleKey.value = preferredKey
        } else if (roles.value.length) {
          selectedRoleKey.value = roles.value[0].key
        } else {
          selectedRoleKey.value = ''
        }
      } catch (err) {
        error.value = err.message
        roles.value = []
        selectedRoleKey.value = ''
      } finally {
        loadingRoles.value = false
      }
    }

    async function loadApplicants() {
      const parsed = parseRoleKey(selectedRoleKey.value)
      if (!parsed) {
        job.value = null
        applicants.value = []
        loading.value = false
        return
      }

      loading.value = true
      error.value = ''
      selectedApplicant.value = null
      try {
        const qs = new URLSearchParams({
          job_id: String(parsed.jobId),
          entry_index: String(parsed.entryIndex),
        })
        const res = await apiFetch(`/jobs/mine/applicants?${qs.toString()}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to load applicants')
        job.value = data.job
        applicants.value = data.applicants || []
      } catch (err) {
        error.value = err.message
        applicants.value = []
      } finally {
        loading.value = false
      }
    }

    async function onJobSelected() {
      const parsed = parseRoleKey(selectedRoleKey.value)
      if (parsed) {
        await router.replace({
          name: 'applicants',
          params: { id: String(parsed.jobId) },
          query: { entry: String(parsed.entryIndex) },
        })
      }
      await loadApplicants()
    }

    async function setStatus(app, nextStatus) {
      busyForId.value = app.id
      try {
        const res = await apiFetch(`/applications/${app.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: nextStatus }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to update status')

        const updatedStatus = data?.application?.status || nextStatus
        const idx = applicants.value.findIndex((a) => a.id === app.id)
        if (idx >= 0) applicants.value[idx].status = updatedStatus
      } catch (err) {
        console.error(err)
      } finally {
        busyForId.value = null
      }
    }

    async function openApplicant(app) {
      selectedApplicant.value = app

      // Mark as viewed only when opening the detail for the first time.
      if (app.status === 'submitted') {
        await setStatus(app, 'viewed')
      }
    }

    function closeApplicant() {
      selectedApplicant.value = null
    }

    onMounted(async () => {
      await loadRoles()
      await loadApplicants()
    })

    return {
      job,
      roles,
      applicants,
      filteredApplicants,
      loadingRoles,
      loading,
      error,
      emptyMessage,
      selectedApplicant,
      busyForId,
      selectedRoleKey,
      statusFilter,
      formatDate,
      cvFilesFor,
      appStatusLabel,
      appStatusClass,
      onJobSelected,
      openApplicant,
      closeApplicant,
      setStatus,
    }
  },
}
</script>
